import React, { useState, useEffect, Fragment, useRef } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { Modal } from '../../components/_EOS/Modal'
import { useApi } from '../../hooks/useApi'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { usePagination } from '../../hooks/usePagination'
import { Status } from '../../hooks/useFetchState'
import { Order } from '../../interfaces/Order/Order'
import { Filter, FilterExtraAction } from '../../components/_WMS/FilterComponent/types'
import { GenericOrderBy } from '../../components/_EOS/Table/types'
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import { Cancel, Visibility, CloudUpload, Add, Merge, Delete } from '@mui/icons-material'
import { useCourier } from '../../hooks/useCourier'
import { useTypesOrder } from '../../hooks/useTypeOrder'
import { useWarehouses } from '../../hooks'
import { useProductOrigins } from '../../hooks/useProductOrigins'
import { useSettings } from '../../hooks/useSettings'
import { eSetting } from '../../enums/eSetting'
import { eTypeSetting } from '../../enums/eTypeSetting'
import { getOrders } from './config/api'
import { updateFilterOptions, defaultFilters } from './config/filterOptions'
import { getColumnsConfig } from './config/tableColumns'
import { ApiResponse } from '../../typings/ApiResponse'
import { OrderFileUpload } from './components/OrderFileUpload'
import { InternalOrderUpload } from './components/InternalOrderUpload'
import { OrderCancellation } from './components/OrderCancellation'
import { IncorporateToWorkOrder } from './components/IncorporateToWorkOrder'
import {
  CreateWorkOrder,
  CreateWorkOrderRef
} from './components/CreateWorkOrder'
import { OrderDetailModal } from './components/OrderDetailModal'
import { FilterComponent } from '../../components/_WMS/FilterComponent'
import CustomAlert from '../../components/_WMS/AlertMessage/AlertMessage'

interface OrderPageProps {}

export const OrderPage: React.FC<OrderPageProps> = () => {
  const createWorkOrderRef = useRef<CreateWorkOrderRef>(null)

  // ==========================================
  // HOOKS DE ESTADO
  // ==========================================

  // Estado principal de datos
  const { state, callApi } = useApi<ApiResponse<Order>>()

  // Filtros
  const [selectedFilters, setSelectedFilters] =
    useState<Filter[]>(defaultFilters)

  // Paginación
  const pagination = usePagination(5)

  // Selección múltiple
  const [selectedElements, setSelectedElements] = useState<number[]>([])

  // Ordenamiento
  const [orderBy, setOrderBy] = useState<GenericOrderBy<Order>>({
    field: 'courierDeliveryDate',
    direction: 'asc'
  })

  // Notificaciones
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  // ==========================================
  // HOOKS DE MODALES
  // ==========================================

  const editModal = useModal<{ order: Order }>('Editar Pedido')
  const createWorkOrderModal = useModal<{ selectedOrders: number[] }>(
    'Crear Orden de Trabajo'
  )
  const deleteModal = useModal<{ orderId: number }>('Eliminar Pedido')
  const orderDetailModal = useModal<{ orderId: number }>('Detalles del Pedido')

  // Nuevos modales
  const uploadOrderFileModal = useModal('Cargar Filtro Externo')
  const internalOrderModal = useModal('Crear Pedido Interno')
  const cancelOrdersModal = useModal('Cancelar Pedidos')
  const incorporateToWOModal = useModal('Incorporar a Orden de Trabajo')

  // ==========================================
  // HOOKS DE DATOS EXTERNOS
  // ==========================================

  const {
    couriersState: { couriers },
    getCouriers
  } = useCourier()
  const {
    typesOrderState: { typesOrder },
    getTypesOrder
  } = useTypesOrder()
  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()
  const {
    getProductOrigins,
    productOriginsState: { productOrigins }
  } = useProductOrigins()
  const { settingsState, getSettings, resetSettings } = useSettings()

  // ==========================================
  // ESTADO ADICIONAL
  // ==========================================

  const [, setSelectedWarehouse] = useState<number>(0)

  const selectedBrandId =
    selectedFilters.find((f) => f.key === 'productOriginId')?.value ?? null

  useEffect(() => {
    if (selectedBrandId != null) {
      getSettings(eTypeSetting.Automatizaciones, selectedBrandId)
    } else {
      resetSettings(eTypeSetting.Automatizaciones)
    }
  }, [selectedBrandId])

  const allowMultipleOrders =
    selectedBrandId == null
      ? false
      : settingsState.settings.find(
          (s) => s.settingsMetadataId === eSetting.AllowMultipleOrdersPerWorkOrder
        )?.value === 'true'

  // ==========================================
  // CONFIGURACIÓN
  // ==========================================

  // Configuración de filtros
  const filterOptions = updateFilterOptions(
    couriers || [],
    typesOrder || [],
    warehouses || [],
    productOrigins || []
  )

  // Configuración de tabla
  const columnsConfig = getColumnsConfig({
    couriers: couriers || [],
    typeOrders: typesOrder || []
  })

  // ==========================================
  // FUNCIONES DE DATOS
  // ==========================================

  // Función principal para obtener datos
  const requestData = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    const filtersToUse = filters !== undefined ? filters : selectedFilters
    const pageToUse = page !== undefined ? page : pagination.page
    const pageSizeToUse =
      pageSize !== undefined ? pageSize : pagination.pageSize

    callApi(getOrders(filtersToUse, pageToUse, pageSizeToUse, orderBy))
      .then(() => {
        setSelectedElements([]) // Limpiar selección al cargar nuevos datos
      })
      .catch((error) => {
        openSnackBar(`Error al cargar datos: ${error}`, false)
      })
  }

  // ==========================================
  // HANDLERS DE TABLA
  // ==========================================

  // Manejo de paginación
  const handlePaginationChange = (page: number, pageSize: number) => {
    const targetPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, targetPage, pageSize)
    pagination.setPagination(targetPage, pageSize)
  }

  // Manejo de selección masiva
  const handleCheckAll = (selectAll: boolean) => {
    if (selectAll) {
      const allIds = state.data?.items.map((item) => item.id) || []
      setSelectedElements(allIds)
    } else {
      setSelectedElements([])
    }
  }

  // ==========================================
  // HANDLERS DE ACCIONES
  // ==========================================

  // Abrir modal de eliminación
  const handleDelete = (row: Order) => {
    deleteModal.openModal(
      'sm',
      { orderId: row.id },
      `Eliminar Pedido #${row.id}`
    )
  }

  // Abrir modal de vista
  const handleView = (row: Order) => {
    orderDetailModal.openModal('lg', { orderId: row.id }, `Detalles del Pedido #${row.id}`)
  }

  // Funciones de éxito para modales
  const handleModalSuccess = (message: string) => {
    openSnackBar(message, true)
    requestData() // Refrescar datos
  }

  // ==========================================
  // HANDLERS PARA NUEVAS ACCIONES
  // ==========================================

  // Handler para crear orden de trabajo
  const handleCreateWorkOrder = () => {
    createWorkOrderModal.openModal('lg', { selectedOrders: selectedElements })
  }

  // Handler para abrir modal de cargar filtro externo
  const handleOpenUploadOrderFile = () => {
    uploadOrderFileModal.openModal('md')
  }

  // Handler para abrir modal de crear pedido interno
  const handleOpenInternalOrder = () => {
    internalOrderModal.openModal('md')
  }

  // Handler para abrir modal de cancelar pedidos
  const handleOpenCancelOrders = () => {
    if (selectedElements.length === 0) {
      openSnackBar('Debe seleccionar al menos un pedido', false)
      return
    }
    cancelOrdersModal.openModal('md')
  }

  // Handler para abrir modal de incorporar a OT
  const handleOpenIncorporateToWO = () => {
    if (selectedElements.length === 0) {
      openSnackBar('Debe seleccionar al menos un pedido', false)
      return
    }
    incorporateToWOModal.openModal('md')
  }

  // Manejador para cambio de filtros
  const handleFilterChange = (value: React.SetStateAction<Filter[]>) => {
    let newFilters: Filter[]

    if (typeof value === 'function') {
      newFilters = value(selectedFilters)
    } else {
      newFilters = value
    }

    // Definimos la clave del filtro que NO queremos que se borre
    const mandatoryKey = 'ordersWithWorkOrder'

    // Verificamos si este filtro existía en la selección anterior
    const mandatoryFilter = selectedFilters.find((f) => f.key === mandatoryKey)

    // Si existía antes, pero NO está en los nuevos filtros (el usuario intentó borrarlo con la X)
    const isMissingInNew = !newFilters.some((f) => f.key === mandatoryKey)

    if (mandatoryFilter && isMissingInNew) {
      newFilters = [...newFilters, mandatoryFilter]
      openSnackBar('El filtro "Incuir pedidos con OT" es obligatorio', false)
    }

    setSelectedFilters(newFilters)
  }

  const handleOnChangeCheckbox = (
    key: string,
    displayValue: string,
    _changeEvent: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    let newFilters: Filter[]

    // 1. Primero, quitamos el filtro que estamos por actualizar (si existía)
    const otherFilters = selectedFilters.filter((el) => el.key !== key)

    // 2. Ahora, decidimos cómo volver a agregarlo
    if (key === 'ordersWithWorkOrder') {
      const dynamicDisplayValue = checked ? 'Sí' : 'No'
      // CASO ESPECIAL: Este filtro siempre se envía.
      // Simplemente lo agregamos de vuelta con el valor 'checked' (sea true o false).
      newFilters = [
        ...otherFilters,
        {
          key: key,
          displayValue: dynamicDisplayValue, // 3. Usa el valor dinámico ("Sí" o "No")
          label: displayValue, // 4. Usa el argumento original como 'label'
          value: checked
        }
      ]
    } else {
      // CASO NORMAL: Los otros filtros solo se agregan si 'checked' es 'true'.
      if (checked) {
        newFilters = [
          ...otherFilters,
          {
            key: key,
            displayValue: displayValue,
            label: '',
            value: checked
          }
        ]
      } else {
        // Si no está chequeado, simplemente no se vuelve a agregar.
        newFilters = otherFilters
      }
    }

    // Actualizar el estado
    setSelectedFilters(newFilters)

    // Usar los nuevos filtros calculados para la búsqueda
    pagination.reset()
    requestData(newFilters, 0, pagination.pageSize)
  }

  // ==========================================
  // EFFECTS
  // ==========================================

  // Cargar datos externos
  useEffect(() => {
    if (couriers?.length === 0) {
      getCouriers()
    }
  }, [])

  useEffect(() => {
    if (typesOrder?.length === 0) {
      getTypesOrder()
    }
  }, [typesOrder])

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    }
  }, [warehouses])

  useEffect(() => {
    if (productOrigins?.length === 0) {
      getProductOrigins()
    }
  }, [productOrigins])

  // Recargar cuando cambie el ordenamiento
  useEffect(() => {
    if (orderBy.field) {
      pagination.reset() // Volver a página 0
      requestData(selectedFilters, 0, pagination.pageSize)
    }
  }, [orderBy])


  // Si solo hay un warehouse, seleccionarlo automáticamente
  useEffect(() => {
    if (warehouses && warehouses?.length === 1) {
      setSelectedWarehouse(warehouses[0].id)
    }
  }, [warehouses])

  // ==========================================
  // ACCIONES EXTRA
  // ==========================================

  const extraActions: FilterExtraAction[] = [
    {
      label: 'Cargar Filtro Externo',
      onClick: handleOpenUploadOrderFile,
      icon: <CloudUpload />
    },
    {
      label: 'Crear Orden de Trabajo',
      onClick: handleCreateWorkOrder,
      icon: <Add />,
      disabled: selectedElements.length === 0
    },
    {
      label: 'Incorporar a Orden de Trabajo',
      onClick: handleOpenIncorporateToWO,
      icon: <Merge />,
      disabled: selectedElements.length === 0 || !allowMultipleOrders,
      tooltip: selectedBrandId == null
        ? 'Filtre por marca para habilitar esta acción'
        : !allowMultipleOrders
          ? 'La marca seleccionada no permite incorporar pedidos a múltiples Órdenes de Trabajo'
          : undefined
    },
    {
      label: 'Cancelar Pedidos',
      onClick: handleOpenCancelOrders,
      icon: <Delete />,
      disabled: selectedElements.length === 0
    }
  ]

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        {/* Filtros */}
        <div>
          <FilterComponent
            options={filterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={handleFilterChange}
            onSearchClick={(filters) => {
              requestData(filters, pagination.page, pagination.pageSize)
            }}
            includeAddButton={true}
            extraActions={extraActions}
            onAddButtonClick={handleOpenInternalOrder}
            createButtonTooltip='Crear Pedido Interno'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectedFilters.find((filter) => filter.key === 'byUnit')
                    ?.value || false
                }
                onChange={(e, c) =>
                  handleOnChangeCheckbox('byUnit', '1 Unidad Total', e, c)
                }
                name='byUnit'
                id='byUnit'
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            }
            label='1 Unidad Total'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectedFilters.find((filter) => filter.key === 'byMonoSKU')
                    ?.value || false
                }
                onChange={(e, c) =>
                  handleOnChangeCheckbox('byMonoSKU', 'Mismo SKU', e, c)
                }
                name='byMonoSKU'
                id='byMonoSKU'
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            }
            label='Mismo SKU'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectedFilters.find(
                    (filter) => filter.key === 'ordersWithWorkOrder'
                  )?.value || false
                }
                onChange={(e, c) =>
                  handleOnChangeCheckbox(
                    'ordersWithWorkOrder',
                    'Incuir pedidos con OT',
                    e,
                    c
                  )
                }
                name='ordersWithWorkOrder'
                id='ordersWithWorkOrder'
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            }
            label='Incuir pedidos con OT'
          />
        </div>

        {/* Tabla principal */}
        <GenericTableV2<Order>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='id'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePaginationChange
          }}
          selection={{
            selectedIds: selectedElements,
            onSelectionChange: (ids) => setSelectedElements(ids as number[]),
            onSelectAll: handleCheckAll
          }}
          sorting={
            orderBy.field
              ? {
                  field: orderBy.field as keyof Order,
                  direction: orderBy.direction,
                  onSortChange: (field, direction) =>
                    setOrderBy({ field, direction })
                }
              : undefined
          }
          actions={[
            {
              key: 'view',
              label: 'Ver Detalle',
              onClick: handleView,
              icon: <Visibility />
            },
            {
              key: 'delete',
              label: 'Cancelar pedido',
              onClick: handleDelete,
              icon: <Cancel />
            }
          ]}
        />
      </StandardPage>

      {/* ==========================================
           MODALES
          ========================================== */}

      <Modal
        open={editModal.isOpen}
        title={editModal.title}
        maxWidth={editModal.size}
        onClose={editModal.closeModal}
        cancelButton={{
          title: 'Cancelar',
          action: editModal.closeModal
        }}
        successButton={{
          title: 'Guardar',
          action: () => {
            // Implementar lógica de guardado
            handleModalSuccess('Pedido actualizado correctamente')
            editModal.closeModal()
          }
        }}
      >
        {editModal.extendedProps?.order && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Editar Pedido #{editModal.extendedProps.order.id}
            </Typography>
            {/* Aquí iría el formulario de edición */}
            <Typography>
              Referencia: {editModal.extendedProps.order.referenceId}
            </Typography>
            <Typography>
              Cantidad: {editModal.extendedProps.order.quantity}
            </Typography>
          </Box>
        )}
      </Modal>

      <Modal
        open={createWorkOrderModal.isOpen}
        title={createWorkOrderModal.title}
        maxWidth={createWorkOrderModal.size}
        onClose={createWorkOrderModal.closeModal}
        cancelButton={{
          title: 'Cancelar',
          action: createWorkOrderModal.closeModal
        }}
        successButton={{
          title: 'Crear OT',
          action: async () => {
            if (createWorkOrderRef.current) {
              await createWorkOrderRef.current.submit()
            }
          }
        }}
      >
        <CreateWorkOrder
          ref={createWorkOrderRef}
          orders={state.data?.items || []}
          onCanSubmitChange={() => {}}
          selectedOrderIds={
            createWorkOrderModal.extendedProps?.selectedOrders || []
          }
          onSuccess={(message) => {
            openSnackBar(message, true)
            setSelectedElements([])
            requestData()
            createWorkOrderModal.closeModal()
          }}
          onError={(message) => {
            openSnackBar(message, false)
          }}
        />
      </Modal>

      <Modal
        open={cancelOrdersModal.isOpen}
        title={cancelOrdersModal.title}
        maxWidth={cancelOrdersModal.size}
        onClose={cancelOrdersModal.closeModal}
      >
        <OrderCancellation
          orderIds={selectedElements}
          orders={state.data?.items || []}
          onSuccess={(message) => {
            openSnackBar(message, true)
            setSelectedElements([])
            requestData()
            cancelOrdersModal.closeModal()
          }}
          onError={(message) => {
            openSnackBar(message, false)
            cancelOrdersModal.closeModal()
          }}
          onClose={cancelOrdersModal.closeModal}
        />
      </Modal>

      {/* ==========================================
           NUEVOS MODALES
          ========================================== */}

      {/* Modal: Cargar Filtro Externo */}
      <Modal
        open={uploadOrderFileModal.isOpen}
        title={uploadOrderFileModal.title}
        maxWidth={uploadOrderFileModal.size}
        onClose={uploadOrderFileModal.closeModal}
      >
        <OrderFileUpload
          warehouses={warehouses || []}
          onSuccess={(message) => {
            openSnackBar(message, true)
            requestData()
            uploadOrderFileModal.closeModal()
          }}
          onError={(message) => {
            openSnackBar(message, false)
          }}
        />
      </Modal>

      {/* Modal: Crear Pedido Interno */}
      <Modal
        open={internalOrderModal.isOpen}
        title={internalOrderModal.title}
        maxWidth={internalOrderModal.size}
        onClose={internalOrderModal.closeModal}
      >
        <InternalOrderUpload
          onSuccess={(message) => {
            openSnackBar(message, true)
            requestData()
            internalOrderModal.closeModal()
          }}
          onError={(message) => {
            openSnackBar(message, false)
          }}
          onClose={internalOrderModal.closeModal}
        />
      </Modal>

      {/* Modal: Cancelar Pedidos */}
      <Modal
        open={deleteModal.isOpen}
        title={deleteModal.title}
        maxWidth={deleteModal.size}
        onClose={deleteModal.closeModal}
      >
        <OrderCancellation
          orderIds={[deleteModal.extendedProps?.orderId || 0]}
          orders={state.data?.items || []}
          onSuccess={(message) => {
            openSnackBar(message, true)
            setSelectedElements([])
            requestData()
            deleteModal.closeModal()
          }}
          onError={(message) => {
            openSnackBar(message, false)
            deleteModal.closeModal()
          }}
          onClose={deleteModal.closeModal}
        />
      </Modal>

      {/* Modal: Incorporar a Orden de Trabajo */}
      <Modal
        open={incorporateToWOModal.isOpen}
        title={incorporateToWOModal.title}
        maxWidth={incorporateToWOModal.size}
        onClose={incorporateToWOModal.closeModal}
      >
        <IncorporateToWorkOrder
          selectedOrderIds={selectedElements}
          orders={state.data?.items || []}
          onSuccess={(message) => {
            openSnackBar(message, true)
            setSelectedElements([])
            requestData()
            incorporateToWOModal.closeModal()
          }}
          onError={(message) => {
            openSnackBar(message, false)
            incorporateToWOModal.closeModal()
          }}
          onClose={incorporateToWOModal.closeModal}
        />
      </Modal>

      <Modal
        open={orderDetailModal.isOpen}
        title={orderDetailModal.title}
        maxWidth={orderDetailModal.size}
        onClose={orderDetailModal.closeModal}
      >
        {orderDetailModal.extendedProps?.orderId && (
          <OrderDetailModal
            orderId={orderDetailModal.extendedProps.orderId}
            onClose={orderDetailModal.closeModal}
            handleSnackbar={openSnackBar}
          />
        )}
      </Modal>

      {/* ==========================================
           NOTIFICACIONES
          ========================================== */}

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
